import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// Brand config. `accent` is RGB-255 for jsPDF setFillColor/setDrawColor.
// `logoDataUrl` is optional — caller pre-loads via loadImageAsPng().
const BRANDS = {
  firefly: {
    displayName: "Firefly Drone Systems",
    footerName: "Firefly Drone Systems",
    accent: [255, 204, 51], // #FFCC33 — Tailwind primary
  },
  swarm: {
    displayName: "Swarm Defense",
    footerName: "Swarm Defense",
    accent: [35, 209, 139], // #23D18B — Swarm Defense primary
  },
};

const parseYYMMDD = (s) => {
  if (!s || s.length !== 6) return null;
  const y = 2000 + parseInt(s.slice(0, 2), 10);
  const m = parseInt(s.slice(2, 4), 10) - 1;
  const d = parseInt(s.slice(4, 6), 10);
  const dt = new Date(Date.UTC(y, m, d));
  if (isNaN(dt)) return null;
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m || dt.getUTCDate() !== d) return null;
  return dt;
};

export const yyyyMmDdToYYMMDD = (s) =>
  s && s.length === 10 ? s.replaceAll("-", "").slice(2) : "";

export const parseActivations = (rows) =>
  (rows || [])
    .map((r) => ({ serial: r.serial, yymmdd: r.yymmdd, dt: parseYYMMDD(r.yymmdd) }))
    .filter((r) => r.dt !== null);

export const filterInWindow = (parsed, fromYYMMDD, toYYMMDD) =>
  parsed.filter((a) => a.yymmdd >= fromYYMMDD && a.yymmdd <= toYYMMDD);

// Load any browser-renderable image (SVG, WebP, PNG, JPG, AVIF) and return
// it as a PNG dataURL plus dimensions so the generator can lay it out.
// jsPDF only embeds JPEG/PNG natively; canvas rasterization is the bridge.
// 4× density looks crisp at the standard 72-DPI PDF resolution; bumping
// higher inflates file size with no visible benefit.
export const loadImageAsPng = async (url, ptWidth) => {
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = url;
  });
  const ratio = img.naturalWidth / img.naturalHeight || 1;
  const ptHeight = ptWidth / ratio;
  const pxScale = 4;
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(ptWidth * pxScale);
  canvas.height = Math.round(ptHeight * pxScale);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return { dataUrl: canvas.toDataURL("image/png"), ptWidth, ptHeight };
};

const monthKey = (d) =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
const monthLabel = (d) =>
  d.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });

const isoWeek = (d) => {
  const tgt = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = tgt.getUTCDay() || 7;
  tgt.setUTCDate(tgt.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tgt.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((tgt - yearStart) / 86400000 + 1) / 7);
  return { year: tgt.getUTCFullYear(), week: weekNo };
};
const weekKey = (d) => {
  const { year, week } = isoWeek(d);
  return `${year}-W${String(week).padStart(2, "0")}`;
};
const isoMonday = (d) => {
  const tgt = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const dayNum = tgt.getUTCDay() || 7;
  tgt.setUTCDate(tgt.getUTCDate() - (dayNum - 1));
  return tgt;
};

const fmtDate = (d) =>
  d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });

const fmtShort = (d) =>
  d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

const buildMonthBuckets = (windowStart, windowEnd) => {
  const buckets = [];
  const cursor = new Date(Date.UTC(windowStart.getUTCFullYear(), windowStart.getUTCMonth(), 1));
  while (cursor <= windowEnd) {
    buckets.push({ key: monthKey(cursor), label: monthLabel(cursor), count: 0 });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return buckets;
};

const buildWeekBuckets = (windowStart, windowEnd) => {
  const buckets = [];
  const cursor = new Date(isoMonday(windowStart));
  while (cursor <= windowEnd) {
    const k = weekKey(cursor);
    const weekEnd = new Date(cursor);
    weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);
    buckets.push({
      key: k,
      label: `${k}  (${fmtShort(cursor)} – ${fmtShort(weekEnd)})`,
      count: 0,
    });
    cursor.setUTCDate(cursor.getUTCDate() + 7);
  }
  return buckets;
};

const dateToYYMMDD = (d) => {
  const y = d.getUTCFullYear() % 100;
  const m = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  return `${String(y).padStart(2, "0")}${String(m).padStart(2, "0")}${String(day).padStart(2, "0")}`;
};

// Shared autotable options that apply to every table — drops the generic
// alternating-row striping and the dark-gray header band in favor of a
// hairline-rule editorial style that lets the brand accent on section
// headers do the visual work.
const TABLE_BASE = {
  styles: {
    fontSize: 10,
    cellPadding: { top: 7, right: 8, bottom: 7, left: 8 },
    textColor: [40, 40, 40],
    lineColor: [225, 225, 225],
    lineWidth: 0.4,
  },
  headStyles: {
    fillColor: [255, 255, 255],
    textColor: [110, 110, 110],
    fontSize: 8,
    fontStyle: "bold",
    cellPadding: { top: 6, right: 8, bottom: 6, left: 8 },
    lineColor: [40, 40, 40],
    lineWidth: 0,
  },
  footStyles: {
    fillColor: [248, 248, 248],
    textColor: [40, 40, 40],
    fontStyle: "bold",
    lineColor: [200, 200, 200],
    lineWidth: 0.5,
  },
  // No alternating rows — clean hairline rules between each row only.
  alternateRowStyles: { fillColor: [255, 255, 255] },
};

// Draw a real bar in a body cell. Background track in light neutral, fill
// scaled to ratio in brand accent. Replaces the original `#`-repeat ASCII.
const drawBar = (doc, accent, cell, ratio) => {
  const padX = cell.padding("horizontal") || 4;
  const trackHeight = 7;
  const trackY = cell.y + (cell.height - trackHeight) / 2;
  const trackX = cell.x + padX;
  const trackWidth = cell.width - padX * 2;
  if (trackWidth <= 0) return;
  const safe = Math.max(0, Math.min(1, ratio));
  // Background track
  doc.setFillColor(238, 238, 238);
  doc.roundedRect(trackX, trackY, trackWidth, trackHeight, 1.5, 1.5, "F");
  // Filled portion
  if (safe > 0) {
    doc.setFillColor(...accent);
    const fillW = Math.max(2, trackWidth * safe);
    doc.roundedRect(trackX, trackY, fillW, trackHeight, 1.5, 1.5, "F");
  }
};

/**
 * @param {Object} opts
 * @param {Array<{serial: string, yymmdd: string}>} opts.activations - raw rows from the API
 * @param {Date} opts.windowStart - inclusive start of reporting window (UTC midnight)
 * @param {Date} opts.windowEnd - inclusive end of reporting window (UTC midnight)
 * @param {"firefly"|"swarm"} opts.brand - which brand to render on cover/footer
 * @param {string} [opts.subtitle] - override for the cover subtitle
 * @param {boolean} [opts.includeMonthly] - render the by-month table
 * @param {boolean} [opts.includeWeekly] - render the by-ISO-week table
 * @param {boolean} [opts.includeSerialList] - render an appendix listing each serial + activation date
 * @param {{dataUrl: string, ptWidth: number, ptHeight: number} | null} [opts.logo] - pre-rasterized brand logo
 * @returns {{ totalInWindow: number, monthBuckets: number, weekBuckets: number }}
 */
const generateManufacturingPDF = ({
  activations,
  windowStart,
  windowEnd,
  brand = "firefly",
  subtitle = "Manufacturing Report",
  includeMonthly = true,
  includeWeekly = true,
  includeSerialList = false,
  logo = null,
}) => {
  const brandCfg = BRANDS[brand] || BRANDS.firefly;
  const accent = brandCfg.accent;

  const fromYY = dateToYYMMDD(windowStart);
  const toYY = dateToYYMMDD(windowEnd);
  const inWindow = filterInWindow(parseActivations(activations), fromYY, toYY)
    .sort((a, b) => a.dt - b.dt);

  const monthBuckets = buildMonthBuckets(windowStart, windowEnd);
  const monthIndex = new Map(monthBuckets.map((b, i) => [b.key, i]));
  inWindow.forEach((a) => {
    const i = monthIndex.get(monthKey(a.dt));
    if (i !== undefined) monthBuckets[i].count += 1;
  });

  const weekBuckets = buildWeekBuckets(windowStart, windowEnd);
  const weekIndex = new Map(weekBuckets.map((b, i) => [b.key, i]));
  inWindow.forEach((a) => {
    const i = weekIndex.get(weekKey(a.dt));
    if (i !== undefined) weekBuckets[i].count += 1;
  });

  const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 48;

  // ───────────────────────────────────────────────────────────
  // COVER
  // Operations-doc layout: brand mark top-left, title + window below a
  // hairline rule, headline figure as a tidy data block, meta anchored
  // bottom. One small brand accent — the rule under the title — does the
  // visual work; everything else stays sober.
  // ───────────────────────────────────────────────────────────

  const logoTop = margin;
  if (logo && logo.dataUrl) {
    const maxLogoWidth = 200;
    const maxLogoHeight = 56;
    let drawW = logo.ptWidth;
    let drawH = logo.ptHeight;
    if (drawW > maxLogoWidth) {
      drawH = (drawH * maxLogoWidth) / drawW;
      drawW = maxLogoWidth;
    }
    if (drawH > maxLogoHeight) {
      drawW = (drawW * maxLogoHeight) / drawH;
      drawH = maxLogoHeight;
    }
    doc.addImage(logo.dataUrl, "PNG", margin, logoTop, drawW, drawH);
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(20, 20, 20);
    doc.text(brandCfg.displayName, margin, logoTop + 22);
  }

  // ── Title block ──
  const titleY = margin + 110;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.setTextColor(20, 20, 20);
  doc.text(subtitle.trim() || "Manufacturing Report", margin, titleY);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(110, 110, 110);
  doc.text(
    `${fmtDate(windowStart)} – ${fmtDate(windowEnd)}`,
    margin,
    titleY + 20
  );

  // ── Headline figure block (tidy, not heroic) ──
  const totalWindow = inWindow.length;
  const figY = titleY + 90;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(140, 140, 140);
  doc.text("UNITS MANUFACTURED IN WINDOW", margin, figY, { charSpace: 1.2 });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(48);
  doc.setTextColor(...accent);
  doc.text(totalWindow.toLocaleString(), margin, figY + 44, { charSpace: -1 });

  // ── Meta block, anchored bottom ──
  const metaY = pageH - 100;
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.5);
  doc.line(margin, metaY - 18, pageW - margin, metaY - 18);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(140, 140, 140);
  doc.text("REPORTING WINDOW", margin, metaY, { charSpace: 1.2 });
  doc.text("GENERATED", pageW - margin, metaY, {
    align: "right",
    charSpace: 1.2,
  });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(40, 40, 40);
  doc.text(
    `${fmtDate(windowStart)} – ${fmtDate(windowEnd)}`,
    margin,
    metaY + 16
  );
  doc.text(
    new Date().toLocaleString("en-US", { timeZone: "UTC" }) + " UTC",
    pageW - margin,
    metaY + 16,
    { align: "right" }
  );

  // ───────────────────────────────────────────────────────────
  // MONTHLY TABLE
  // ───────────────────────────────────────────────────────────
  if (includeMonthly) {
    doc.addPage();
    let y = margin + 4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(20, 20, 20);
    doc.text("Drones manufactured", margin, y);
    y += 22;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `By month  ·  ${fmtDate(windowStart)} – ${fmtDate(windowEnd)}`,
      margin,
      y
    );
    y += 14;

const monthTotal = monthBuckets.reduce((s, b) => s + b.count, 0);
    const monthPeak = Math.max(1, ...monthBuckets.map((b) => b.count));
    const monthRatios = monthBuckets.map((b) => b.count / monthPeak);

    autoTable(doc, {
      ...TABLE_BASE,
      startY: y + 4,
      margin: { left: margin, right: margin },
      head: [["MONTH", "UNITS", "SHARE", "DISTRIBUTION"]],
      body: monthBuckets.map((b) => [
        b.label,
        b.count.toLocaleString(),
        monthTotal > 0 ? `${((b.count / monthTotal) * 100).toFixed(1)}%` : "—",
        "",
      ]),
      foot: [["Total", monthTotal.toLocaleString(), "100.0%", ""]],
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 150 },
        1: { halign: "right", cellWidth: 70, font: "courier" },
        2: { halign: "right", cellWidth: 60, font: "courier" },
        3: { cellWidth: "auto" },
      },
      didDrawCell: (data) => {
        if (data.column.index === 3 && data.section === "body") {
          drawBar(doc, accent, data.cell, monthRatios[data.row.index]);
        }
      },
    });
  }

  // ───────────────────────────────────────────────────────────
  // WEEKLY TABLE
  // ───────────────────────────────────────────────────────────
  if (includeWeekly) {
    doc.addPage();
    let y = margin + 4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(20, 20, 20);
    doc.text("Drones manufactured", margin, y);
    y += 22;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `By ISO week  ·  ${fmtDate(windowStart)} – ${fmtDate(windowEnd)}`,
      margin,
      y
    );
    y += 14;

const weekTotal = weekBuckets.reduce((s, b) => s + b.count, 0);
    const weekPeak = Math.max(1, ...weekBuckets.map((b) => b.count));
    const weekRatios = weekBuckets.map((b) => b.count / weekPeak);

    autoTable(doc, {
      ...TABLE_BASE,
      styles: { ...TABLE_BASE.styles, fontSize: 9, cellPadding: { top: 5, right: 8, bottom: 5, left: 8 } },
      startY: y + 4,
      margin: { left: margin, right: margin },
      head: [["ISO WEEK", "UNITS", "DISTRIBUTION"]],
      body: weekBuckets.map((b) => [b.label, b.count.toLocaleString(), ""]),
      foot: [["Total", weekTotal.toLocaleString(), ""]],
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 230 },
        1: { halign: "right", cellWidth: 70, font: "courier" },
        2: { cellWidth: "auto" },
      },
      didDrawCell: (data) => {
        if (data.column.index === 2 && data.section === "body") {
          drawBar(doc, accent, data.cell, weekRatios[data.row.index]);
        }
      },
    });
  }

  // ───────────────────────────────────────────────────────────
  // SERIAL LIST APPENDIX
  // ───────────────────────────────────────────────────────────
  if (includeSerialList) {
    doc.addPage();
    let y = margin + 4;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(20, 20, 20);
    doc.text("Drones manufactured", margin, y);
    y += 22;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(120, 120, 120);
    doc.text(
      `Serial list  ·  ${inWindow.length.toLocaleString()} units, oldest first`,
      margin,
      y
    );
    y += 14;

autoTable(doc, {
      ...TABLE_BASE,
      styles: { ...TABLE_BASE.styles, fontSize: 9, cellPadding: { top: 4, right: 8, bottom: 4, left: 8 } },
      startY: y + 4,
      margin: { left: margin, right: margin },
      head: [["SERIAL NUMBER", "ACTIVATION DATE"]],
      body: inWindow.map((a) => [a.serial || "—", fmtDate(a.dt)]),
      foot: [["Total", inWindow.length.toLocaleString()]],
      columnStyles: {
        0: { fontStyle: "bold", cellWidth: 220, font: "courier" },
        1: { halign: "left", cellWidth: "auto" },
      },
    });
  }

  // ───────────────────────────────────────────────────────────
  // FOOTER (page numbers + brand line)
  // ───────────────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    // Neutral hairline above footer — brand presence on the cover and
    // section accents is enough; per-page brand color reads as too much.
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.4);
    doc.line(margin, pageH - 30, pageW - margin, pageH - 30);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(brandCfg.footerName, margin, pageH - 18);
    doc.text(`Page ${i} of ${totalPages}`, pageW - margin, pageH - 18, {
      align: "right",
    });
  }

  const dateStamp = new Date().toISOString().slice(0, 10);
  const safeBrand = brandCfg.footerName.replace(/\s+/g, "_");
  doc.save(`${safeBrand}_Manufacturing_Report_${dateStamp}.pdf`);

  return {
    totalInWindow: totalWindow,
    monthBuckets: monthBuckets.length,
    weekBuckets: weekBuckets.length,
  };
};

export default generateManufacturingPDF;
